import { useState } from "react";
import { CalendarDays, Clock, Users, MessageCircle } from "lucide-react";
import { Reveal, Horizon } from "../components/Reveal";
import { IMG } from "../data/images";
import { INFO } from "../data/info";
import { useLang } from "../i18n";

const HORAS = ["9:00", "9:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"];
const PERSONAS = [1, 2, 3, 4, 5, 6, 7, 8];

const hoy = () => {
  const date = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const campo =
  "v1-field w-full appearance-none rounded-none border-0 border-b border-[#2B1E16]/20 bg-transparent px-0 py-3 font-serif text-lg text-[#2B1E16] outline-none transition-colors placeholder:text-[#2B1E16]/35 focus:border-[#8A6A4F]";

export const Reserva = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    nombre: "",
    fecha: hoy(),
    hora: "11:00",
    personas: "2",
    nota: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const w = t.reserva.wa;
  const mensaje = [
    w.intro,
    "",
    `${w.name}: ${form.nombre || "—"}`,
    `${w.date}: ${form.fecha}`,
    `${w.time}: ${form.hora}`,
    `${w.people}: ${form.personas}`,
    form.nota ? `${w.note}: ${form.nota}` : null,
  ]
    .filter((l) => l !== null)
    .join("\n");

  const enlace = `https://wa.me/${INFO.whatsapp}?text=${encodeURIComponent(mensaje)}`;

  return (
    <section id="reserva" data-testid="reserva-section" className="bg-[#F5EDE3] px-6 py-24 md:px-12 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid gap-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-24">
          <div>
            <Reveal>
              <p className="label text-[#8A6A4F]">{t.reserva.label}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 max-w-[15ch] font-serif text-4xl font-light leading-[1.06] text-[#2B1E16] sm:text-5xl lg:text-6xl">
                {t.reserva.title}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 max-w-[46ch] font-sans text-sm font-light leading-relaxed text-[#2B1E16]/60 md:text-base">
                {t.reserva.intro}
              </p>
            </Reveal>

            <div className="mt-12 max-w-sm">
              <Horizon />
            </div>

            <Reveal delay={0.2}>
              <form className="v1-reservation-form mt-12 max-w-lg space-y-9" onSubmit={(e) => e.preventDefault()}>
                <div>
                  <label htmlFor="reserva-nombre" className="label text-[#2B1E16]/45">
                    {t.reserva.nameLabel}
                  </label>
                  <input
                    id="reserva-nombre"
                    data-testid="reserva-nombre"
                    type="text"
                    autoComplete="name"
                    placeholder={t.reserva.namePlaceholder}
                    value={form.nombre}
                    onChange={set("nombre")}
                    className={`mt-2 ${campo}`}
                  />
                </div>

                <div className="grid gap-9 sm:grid-cols-3">
                  <div>
                    <label htmlFor="reserva-fecha" className="label flex items-center gap-2 text-[#2B1E16]/45">
                      <CalendarDays size={13} strokeWidth={1.6} /> {t.reserva.dayLabel}
                    </label>
                    <input
                      id="reserva-fecha"
                      data-testid="reserva-fecha"
                      type="date"
                      min={hoy()}
                      value={form.fecha}
                      onChange={set("fecha")}
                      className={`mt-2 ${campo}`}
                    />
                  </div>
                  <div>
                    <label htmlFor="reserva-hora" className="label flex items-center gap-2 text-[#2B1E16]/45">
                      <Clock size={13} strokeWidth={1.6} /> {t.reserva.timeLabel}
                    </label>
                    <select
                      id="reserva-hora"
                      data-testid="reserva-hora"
                      value={form.hora}
                      onChange={set("hora")}
                      className={`mt-2 ${campo}`}
                    >
                      {HORAS.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="reserva-personas" className="label flex items-center gap-2 text-[#2B1E16]/45">
                      <Users size={13} strokeWidth={1.6} /> {t.reserva.peopleLabel}
                    </label>
                    <select
                      id="reserva-personas"
                      data-testid="reserva-personas"
                      value={form.personas}
                      onChange={set("personas")}
                      className={`mt-2 ${campo}`}
                    >
                      {PERSONAS.map((n) => (
                        <option key={n} value={String(n)}>
                          {n}
                        </option>
                      ))}
                      <option value={t.reserva.peopleMore}>{t.reserva.peopleMore}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="reserva-nota" className="label text-[#2B1E16]/45">
                    {t.reserva.noteLabel}
                  </label>
                  <input
                    id="reserva-nota"
                    data-testid="reserva-nota"
                    type="text"
                    placeholder={t.reserva.notePlaceholder}
                    value={form.nota}
                    onChange={set("nota")}
                    className={`mt-2 ${campo}`}
                  />
                </div>

                <div className="pt-2">
                  <a
                    data-testid="reserva-whatsapp-btn"
                    href={enlace}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="v1-reservation-submit btn-dark inline-flex items-center gap-3"
                  >
                    <MessageCircle size={16} strokeWidth={1.6} />
                    {t.reserva.send}
                  </a>
                  <p className="mt-5 font-sans text-xs font-light leading-relaxed text-[#2B1E16]/45">
                    {t.reserva.hint}
                  </p>
                </div>
              </form>
            </Reveal>
          </div>

          <Reveal delay={0.25}>
            <div className="v1-reservation-photo group h-full min-h-[420px] overflow-hidden bg-[#2B1E16]/10">
              <img
                src={IMG.interiorWide}
                alt={t.visit.altTerrace}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[2200ms] ease-out group-hover:scale-105"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
